import { useMutation, useQuery } from '@apollo/react-hooks';
import { gql } from 'graphql-tag';
import React, { useState } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import ButtonV2 from 'components/ButtonV2';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import {
  PageWrapper,
  Container,
  LoadingCard,
  LoadingSpinner,
  LoadingText,
  ErrorCard,
} from 'components/TicketForms';
import AnnualTicketForm from '../components/AnnualTicketForm';

// GraphQL query for fetching annual ticket
const GET_ANNUAL_TICKET = gql`
  query GetAnnualTicket($id: Int!) {
    getAnnualTicket(id: $id) {
      id
      name
      color_code
      price
      description
      description_image_id
      description_image_url
      description_video
      track_price
      fee_dollar
      fee_percent
      is_featured
      featured_text
      sale_start
      on_sale_time
      sale_end
      off_sale_time
      start_date
      end_date
      status
      generate_passes
      display_events
      display_track_series
      for_all_events
      limit
      low_ticket
      account_id
      show_fee
      international_fee
      marketing_fee
      track_ids
      series_ids
      event_ids
      set_member_number
      last_member_number
      prefix_text
      suffix_text
      ticket_type
      visibility
      category_id
      reserved_seat
      reserved_seat_category
      reserved_seat_prefix
      reserved_seat_section
      tracks {
        id
        name
      }
      series {
        id
        name
      }
      events {
        id
        name
      }
      pages {
        id
        name
        type
        form_id
        order
        pdf_url
        pdf_id
        video_url
        waivers
      }
      windows {
        id
        name
        ends
        time
        price
        tiers {
          id
          name
          price
          track_price
        }
      }
      tiers {
        id
        name
        price
        track_price
      }
    }
  }
`;

// GraphQL mutation for updating annual ticket (V2)
const UPDATE_ANNUAL_TICKET_V2 = gql`
  mutation UpdateAnnualTicketV2($input: UpdateAnnualTicketInputV2!) {
    updateAnnualTicketV2(input: $input) {
      id
      name
    }
  }
`;

const EditAnnualTicketContent = () => {
  const { id: ticketId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const [saving, setSaving] = useState(false);

  // Determine route prefix for navigation
  const { pathname } = location;
  const isTrackAdmin = pathname?.includes('admin-track');
  const isEmployeeAdmin = pathname?.includes('admin-employee');
  const routePrefix = isTrackAdmin
    ? '/admin-track'
    : isEmployeeAdmin
    ? '/admin-employee'
    : '/admin';

  // Fetch existing ticket data
  const {
    data: ticketData,
    loading: ticketLoading,
    error: ticketError,
  } = useQuery(GET_ANNUAL_TICKET, {
    variables: { id: parseInt(ticketId) },
    skip: !ticketId,
    fetchPolicy: 'cache-and-network',
  });

  const ticket = ticketData?.getAnnualTicket;

  // Mutation
  const [updateAnnualTicketV2] = useMutation(UPDATE_ANNUAL_TICKET_V2);

  const handleSubmit = async (formData) => {
    setSaving(true);

    try {
      const { data } = await updateAnnualTicketV2({
        variables: {
          input: {
            id: parseInt(ticketId),
            account_id: ticket?.account_id,
            ...formData,
          },
        },
      });

      if (data?.updateAnnualTicketV2) {
        // Preserve account_id in redirect URL
        const accountId = ticket?.account_id;
        history.push({
          pathname: `${routePrefix}/annualTickets`,
          search: accountId ? `?account_id=${accountId}` : '',
          state: {
            toaster: {
              type: 'success',
              title: 'Ticket Updated',
              message: `"${formData.name}" has been updated successfully.`,
            },
          },
        });
      }
    } catch (error) {
      setSaving(false);
      throw error; // Re-throw to let the form handle the error display
    }
  };

  const handleCancel = () => {
    // Preserve account_id in redirect URL
    const accountId = ticket?.account_id;
    const backUrl = accountId
      ? `${routePrefix}/annualTickets?account_id=${accountId}`
      : `${routePrefix}/annualTickets`;
    history.push(backUrl);
  };

  // Loading state
  if (ticketLoading) {
    return (
      <PageWrapper>
        <Container>
          <LoadingCard>
            <LoadingSpinner />
            <LoadingText>Loading ticket...</LoadingText>
          </LoadingCard>
        </Container>
      </PageWrapper>
    );
  }

  // Error state
  if (ticketError) {
    return (
      <PageWrapper>
        <Container>
          <ErrorCard>
            <p>Error loading ticket: {ticketError.message}</p>
            <ButtonV2 onClick={handleCancel}>Go Back</ButtonV2>
          </ErrorCard>
        </Container>
      </PageWrapper>
    );
  }

  return (
    <AnnualTicketForm
      mode="edit"
      initialData={ticket}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      saving={saving}
      accountId={ticket?.account_id}
    />
  );
};

// Wrap with theme provider
const EditAnnualTicket = () => (
  <EnhancedThemeProvider>
    <EditAnnualTicketContent />
  </EnhancedThemeProvider>
);

export default EditAnnualTicket;
